import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Bell, Check, CheckCheck, Trash2, MessageSquare, Wrench, AlertTriangle,
  Info, Filter, Loader2, Inbox
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ICONS = {
  mention: MessageSquare,
  task_assigned: Wrench,
  follow_up_warning: AlertTriangle,
  follow_up_urgent: AlertTriangle,
  follow_up_escalation: AlertTriangle,
  follow_up_neglected: AlertTriangle,
  delivery_reminder: Info,
  measurement_reminder: Info,
  inactive_project: Info,
  lead_submission: Inbox,
};

const FILTERS = [
  { value: 'all', label: 'Wszystkie' },
  { value: 'unread', label: 'Nieprzeczytane' },
  { value: 'mention', label: 'Oznaczenia' },
  { value: 'follow_up', label: 'Przypomnienia' },
  { value: 'lead_submission', label: 'Leady' },
];

const formatTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'teraz';
  if (diffMins < 60) return `${diffMins} min temu`;
  if (diffHours < 24) return `${diffHours} godz. temu`;
  if (diffDays < 7) return `${diffDays} dni temu`;
  return date.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const navigate = useNavigate();
  const LIMIT = 30;

  const fetchNotifications = useCallback(async (reset = false) => {
    const p = reset ? 1 : page;
    setLoading(true);
    try {
      const params = { limit: LIMIT, skip: (p - 1) * LIMIT };
      if (filter === 'unread') params.unread_only = true;
      if (filter !== 'all' && filter !== 'unread') params.type_filter = filter;
      const res = await axios.get(`${API_URL}/notifications`, { params });
      const data = res.data;
      if (reset) {
        setNotifications(data);
        setPage(1);
      } else {
        setNotifications(prev => p === 1 ? data : [...prev, ...data]);
      }
      setHasMore(data.length === LIMIT);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [filter, page]);

  useEffect(() => { fetchNotifications(true); }, [filter]);

  const handleClick = async (n) => {
    if (!n.is_read) {
      try {
        await axios.patch(`${API_URL}/notifications/${n.id}/read`);
        setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true } : x));
      } catch { /* ignore */ }
    }
    if (n.link) navigate(n.link);
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.patch(`${API_URL}/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch { /* ignore */ }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API_URL}/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch { /* ignore */ }
  };

  const loadMore = () => {
    setPage(p => p + 1);
    fetchNotifications(false);
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <Layout>
      <div className="p-4 md:p-8 lg:p-12 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-4xl font-manrope font-bold text-[var(--crm-text-primary)] tracking-tight flex items-center gap-3">
              <Bell className="w-7 h-7 text-[#FF6B35]" />
              Powiadomienia
            </h1>
            <p className="text-[var(--crm-text-secondary)] mt-1 text-sm">
              {unreadCount > 0 ? `${unreadCount} nieprzeczytanych` : 'Wszystko przeczytane'}
            </p>
          </div>
          {unreadCount > 0 && (
            <Button onClick={handleMarkAllRead} data-testid="mark-all-read-page"
              className="bg-[var(--crm-bg-btn-secondary)] hover:bg-[var(--crm-bg-btn-secondary-hover)] text-[var(--crm-text-primary)] text-sm">
              <CheckCheck className="w-4 h-4 mr-2" />
              Oznacz wszystkie
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          {FILTERS.map(f => (
            <Button key={f.value}
              onClick={() => setFilter(f.value)}
              data-testid={`notif-filter-${f.value}`}
              className={`h-9 px-4 text-sm rounded-full transition-all ${
                filter === f.value
                  ? 'bg-[#FF6B35] text-white'
                  : 'bg-[var(--crm-bg-card)] text-[var(--crm-text-secondary)] border border-[var(--crm-border)] hover:border-[var(--crm-text-muted)]'
              }`}
            >
              {f.label}
            </Button>
          ))}
        </div>

        {/* List */}
        {loading && notifications.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#FF6B35] animate-spin" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-20">
            <Bell className="w-16 h-16 text-[var(--crm-text-muted)] mx-auto mb-4 opacity-30" />
            <p className="text-[var(--crm-text-secondary)] text-lg">Brak powiadomień</p>
          </div>
        ) : (
          <div className="space-y-2">
            {notifications.map(n => {
              const Icon = ICONS[n.type] || Bell;
              const isAlert = n.type?.includes('follow_up') || n.type?.includes('escalation');
              return (
                <Card key={n.id} onClick={() => handleClick(n)}
                  data-testid={`notif-item-${n.id}`}
                  className={`cursor-pointer border-[var(--crm-border)] hover:border-[var(--crm-text-muted)] transition-all group ${
                    !n.is_read
                      ? 'bg-[#FF6B35]/[0.05] border-l-4 border-l-[#FF6B35]'
                      : 'bg-[var(--crm-bg-card)]'
                  }`}
                >
                  <div className="flex gap-3 p-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                      isAlert ? 'bg-amber-500/15 text-amber-500'
                        : !n.is_read ? 'bg-[#FF6B35]/15 text-[#FF6B35]'
                        : 'bg-[var(--crm-bg-btn-secondary)] text-[var(--crm-text-muted)]'
                    }`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm font-semibold leading-snug ${
                          !n.is_read ? 'text-[var(--crm-text-primary)]' : 'text-[var(--crm-text-secondary)]'
                        }`}>
                          {n.title}
                        </p>
                        <span className="text-[11px] text-[var(--crm-text-muted)] whitespace-nowrap shrink-0">
                          {formatTime(n.created_at)}
                        </span>
                      </div>
                      <p className="text-xs text-[var(--crm-text-secondary)] mt-1 line-clamp-2">{n.message}</p>
                      <div className="flex items-center justify-between mt-2">
                        {n.project_name && (
                          <span className="text-xs text-[#FF6B35] font-medium truncate max-w-[250px]">{n.project_name}</span>
                        )}
                        <button onClick={(e) => handleDelete(e, n.id)}
                          className="opacity-0 group-hover:opacity-100 text-[var(--crm-text-muted)] hover:text-red-500 transition-opacity p-1 ml-auto">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
            {hasMore && (
              <Button onClick={loadMore} disabled={loading}
                className="w-full bg-[var(--crm-bg-card)] border border-[var(--crm-border)] text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)] hover:bg-[var(--crm-bg-card-hover)]">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Załaduj więcej
              </Button>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};
